import { FCC } from "@dgs/core";
import { addDays, format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useLandingPageConsents } from "../../shared/api/registrationForms";
import { CompanionsByType, RegistrationFormResource } from "../../shared/types";
import {
	HotelSectionFormValues,
	HotelStayFormValues,
	HotelStayStatus,
	HotelSummaryResource,
	StayConfigurationResource,
	WayOfBooking,
} from "../../shared/types/hotel";
import { GuestTravelFormValues } from "../../shared/types/travel";
import { findNextStep, findPrevStep } from "../registrationFormUtils";
import { ConsentValues, CurrentCompanion, RegistrationFormContext } from "./registrationFormProviderContext";

/*
	Der PreviewRegistrationFormProvider implementiert den RegistrationFormContext mit der Preview Funktionalität, indem
	die Conditions nicht dafür sorgen, dass die Steps/Sections/Data fields ausgeblendet werden.
	Dazu geben die getHidden Funktionen alle hard coded leere array zurück.
	Weiterhin wird die Submit Funktion als Mock implementiert
*/

export const PreviewRegistrationFormProvider: FCC<{ registrationForm: RegistrationFormResource }> = ({
	registrationForm,
	children,
}) => {
	const [searchParams] = useSearchParams();
	const token = useMemo(
		() => sessionStorage.getItem("registrationToken") || searchParams.get("token") || "",
		[searchParams],
	);
	const [filledSteps, setFilledSteps] = useState<Record<number, Record<string, any>>>({});
	const [givenConsents, setGivenConsents] = useState<ConsentValues>({});
	const [companionsByType, setCompanionsByType] = useState<CompanionsByType[]>([]);
	const [travels, setTravels] = useState<GuestTravelFormValues[]>([]);
	const errors = {};
	const navigate = useNavigate();
	const { landingConsents, isLoading: areConsentsLoading } = useLandingPageConsents(registrationForm.id, null);

	const submitStep = useCallback(
		async (stepId: number, values: Record<string, any>) => {
			const newFilledSteps = {
				...filledSteps,
				[stepId]: values,
			};
			setFilledSteps(newFilledSteps);
			return findNextStep([], registrationForm.steps, stepId);
		},
		[filledSteps, registrationForm.steps],
	);

	const submitRegistration = () => Promise.resolve(true);
	const declineRegistration = () => {
		navigate("./declined", { replace: true });
		return Promise.resolve();
	};
	const cancelRegistration = () => {
		navigate("./cancelled", { replace: true });
		return Promise.resolve();
	};
	const nominateReplacement = () => {
		navigate("./replaced", { replace: true });
		return Promise.resolve();
	};
	const validateStep = () => ({});
	const validateStaticStep = () => ({});
	const previousStep = useCallback(
		(currentStepId?: number) => {
			return findPrevStep([], registrationForm.steps, currentStepId ?? null);
		},
		[registrationForm.steps],
	);

	const submitLanding = useCallback(
		(consents: ConsentValues) => {
			setGivenConsents((current) => ({ ...current, ...consents }));
			return findNextStep([], registrationForm.steps, null);
		},
		[registrationForm.steps],
	);

	const saveCompanion = useCallback(
		(currentCompanion: CurrentCompanion) => {
			if (companionsByType.find((c) => c.id === currentCompanion.companionType.id) === undefined) {
				companionsByType.push({
					id: currentCompanion.companionType.id,
					singularLabel: currentCompanion.companionType.singularLabel,
					pluralLabel: currentCompanion.companionType.pluralLabel,
					companions: [currentCompanion.companion],
				});
				setCompanionsByType(companionsByType);
				return;
			}

			const updatedCompanions = companionsByType.map((group) => {
				if (group.id === currentCompanion.companionType.id) {
					const newCompanions = group.companions.map((c, index) =>
						index === currentCompanion.index ? currentCompanion.companion : c,
					);

					const orderedNewCompanions =
						group.companions.findIndex((_, index) => currentCompanion.index === index) >= 0
							? newCompanions
							: newCompanions.concat(currentCompanion.companion);

					return { ...group, companions: orderedNewCompanions };
				}

				return group;
			});

			setCompanionsByType(updatedCompanions);
		},
		[companionsByType, setCompanionsByType],
	);

	const removeCompanion = useCallback(
		(indexToRemove: number, companionTypeId: number) => {
			const updatedCompanions = companionsByType.map((group) => {
				if (group.id === companionTypeId) {
					const newCompanions = group.companions.filter((c, index) => index !== indexToRemove);
					return { ...group, companions: newCompanions };
				}
				return group;
			});

			setCompanionsByType(updatedCompanions);
		},
		[companionsByType, setCompanionsByType],
	);

	const clearRegistration = useCallback(() => {
		setGivenConsents({});
		setFilledSteps([]);
		setCompanionsByType([]);
	}, [setCompanionsByType, setGivenConsents]);

	useEffect(() => {
		token && token !== sessionStorage.getItem("token") && sessionStorage.setItem("registrationToken", token);
	}, [token]);

	const companionFormValidation = () => ({});
	const validateCompanions = () => ({});

	const getStayConfigurationValidation =
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(stayConfiguration: StayConfigurationResource) => (values: HotelSectionFormValues | null) => undefined;

	const getHotelStayValidation =
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(stayConfiguration: StayConfigurationResource, otherStays: HotelStayFormValues[]) =>
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(values: HotelStayFormValues | null) =>
				undefined;

	const getHotelSummary = useCallback((): { data: HotelSummaryResource[]; isLoading: boolean } => {
		return {
			data: [
				{
					checkIn: format(new Date(), "yyyy-MM-dd"),
					checkOut: format(addDays(new Date(), 1), "yyyy-MM-dd"),
					status: HotelStayStatus.INQUIRY,
					hotelName: "Preview Hotel",
					roomTypeName: "Preview Room",
					currency: { name: "EUR", isoCode: "EUR", minorUnit: 2 },
					prices: [{ date: format(new Date(), "yyyy-MM-dd"), price: 1000 }],
					totalPrice: 1000,
					stayConfiguration: {
						hotelStayTypeId: 1,
						earliestCombinedCheckIn: format(new Date(), "yyyy-MM-dd"),
						latestCombinedCheckOut: format(addDays(new Date(), 1), "yyyy-MM-dd"),
						earliestCheckIn: format(new Date(), "yyyy-MM-dd"),
						latestCheckOut: format(addDays(new Date(), 1), "yyyy-MM-dd"),
						prefillCheckInCheckOutInFrontend: false,
						minStayPeriods: 1,
						maxStayPeriods: 2,
						maxNights: 1,
						isPreferredHotelSelectable: true,
						isPreferredRoomTypeSelectable: true,
						showRoomCapacityInFrontend: true,
						wayOfBooking: WayOfBooking.INQUIRY,
						allowAdditionalPersons: true,
						allowRemarkByGuest: true,
						enableQuotaWarning: false,
					},
				},
			],
			isLoading: false,
		};
	}, []);

	return (
		<RegistrationFormContext.Provider
			value={{
				errors,
				registrationForm,
				validateStep,
				validateStaticStep,
				previousStep,
				submitStep,
				submitRegistration,
				filledSteps,
				givenConsents,
				companionsByType,
				saveCompanion,
				removeCompanion,
				submitLanding,
				hiddenSteps: [],
				getHiddenSections: () => [],
				getHiddenDataFields: () => [],
				isPreview: true,
				clearRegistration,
				declineRegistration,
				cancelRegistration,
				nominateReplacement,
				travels,
				setTravels,
				landingConsents,
				areConsentsLoading,
				companionFormValidation,
				validateCompanions,
				getStayConfigurationValidation,
				getHotelStayValidation,
				getHotelSummary,
				submitRegistrationFormDataForShopSummary: () => Promise.resolve({} as any),
				isShopEnabled: false,
				hasSeenLanding: true,
				setHasSeenLanding: () => {},
			}}
		>
			{children}
		</RegistrationFormContext.Provider>
	);
};
